/**
 * cancelReasons — canonical session cancellation reasons (WCO-2706).
 *
 * The selected code is what gets persisted (backend appends it to
 * ics.hte_production_session.notes as "Cancelled: <reason>" and to
 * ics.hte_production_session_history.notes). Picking `other` stores the
 * manager's typed text verbatim instead of a code.
 *
 * Key rule: never inline the reason list or a code→label map — import from here
 * so the modal, the History tab and the Cancelled sessions table stay in sync.
 */

/** Reason codes, in dropdown order. Labels live in i18n `sessionDetails.cancelReasons.*`. */
export const CANCEL_REASONS = [
  'wrong_product',
  'duplicate',
  'entered_in_error',
  'other',
] as const;

export type CancelReasonCode = (typeof CANCEL_REASONS)[number];

/**
 * Translate a stored cancellation reason for display.
 * Known codes render their localized label; anything else (an `other` free-text
 * reason, or a legacy value such as "Cancelled by manager") renders verbatim.
 */
export function formatCancelReason(raw: string | null | undefined, t: (key: string) => string): string {
  const value = (raw || '').trim();
  if (!value) return '';
  const key = `sessionDetails.cancelReasons.${value}`;
  const translated = t(key);
  return translated === key ? value : translated;
}
